import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import Editor from "@/design/components/Editor";
import EditorList from "@/design/components/EditorList";
import EditorSidebar from "@/design/components/EditorSidebar";
import TabPanel from "@/design/components/TabPanel";
import LineKeyType from "@/utils/enums/LineKeyType";
import Valkyrie, { viTrashCan } from "@sippy-platform/valkyrie";

import { Box, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Tab, Tabs } from "@mui/material";
import PropTypes from "prop-types";

import LineKeyForm from "./LineKeyForm";
import LineKeyIcon from "./LineKeyIcon";

function LineKeyEditor({ lineKeyTemplate, specs, lineKeyTemplateDispatch, customHeader }) {
  const pbx = useSelector((state) => state.domain.pbx);

  const [page, setPage] = useState(1);
  const [activeLineKey, setActiveLineKey] = useState(null);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getLineKeyTypes());
  }, []);

  const deviceLineKeys = useMemo(
    () => ({
      supportedTypes: specs?.lineKeyTypes ?? [],
      pages: specs?.lineKeyPages ?? 0,
      count: specs?.lineKeys ?? 0,
    }),
    [specs],
  );

  // Pages and tabs
  const pageList = useMemo(() => {
    const pageList = [];

    for (let _pageNumber = 1; _pageNumber <= deviceLineKeys.pages; _pageNumber++) {
      pageList.push({ value: _pageNumber, label: `Page ${_pageNumber}` });
    }

    return pageList;
  }, [deviceLineKeys.pages]);

  const pageSize = useMemo(
    () => deviceLineKeys.count / deviceLineKeys.pages,
    [deviceLineKeys.count, deviceLineKeys.pages],
  );

  // Line key list
  const lineKeyList = useMemo(() => {
    const lineKeyList = Array.from({ length: deviceLineKeys.count }, (_, index) => {
      const location = index + 1;

      return (
        lineKeyTemplate.lineKeys?.find((_lineKey) => _lineKey.location === location) ?? {
          location,
          extension: "",
          id: null,
          label: null,
          line: null,
          type: {
            type: "Empty",
            lineKeyId: 0,
          },
          value: null,
        }
      );
    });

    return lineKeyList.slice((page - 1) * pageSize, page * pageSize);
  }, [deviceLineKeys.count, lineKeyTemplate.lineKeys, pageSize, page]);

  function clearLineKey(lineKey) {
    lineKeyTemplateDispatch({ type: "deleteLineKey", value: lineKey });

    Promise.resolve(Api.deleteLineKeyTemplateLineKey(lineKeyTemplate, lineKey)).catch(() => {
      lineKeyTemplateDispatch({ type: "addLineKey", value: lineKey });
    });

    if (lineKey.location === activeLineKey?.location) {
      setActiveLineKey(null);
    }
  }

  return (
    <Editor
      title="Line keys"
      id="lineKeyEditorCard"
      customHeader={customHeader}
      tabs={
        <>
          {pageList?.length >= 2 && (
            <Tabs value={page} onChange={(e, _page) => setPage(_page)} sx={{ pt: 1 }}>
              {pageList.map((_page) => (
                <Tab key={_page.value} value={_page.value} label={_page.label} />
              ))}
            </Tabs>
          )}
        </>
      }
    >
      <Box>
        {pageList.map((_page) => (
          <TabPanel key={`key-${_page.value}`} value={page} index={_page.value}>
            <EditorList columns={2} rows={Math.ceil(pageSize / 2)} direction="column">
              <>
                {lineKeyList.map((lineKey) => (
                  <ListItem
                    key={lineKey.location}
                    disablePadding
                    secondaryAction={
                      lineKey.type?.lineKeyId !== 0 && (
                        <IconButton onClick={() => clearLineKey(lineKey)}>
                          <Valkyrie icon={viTrashCan} />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemButton
                      selected={activeLineKey?.location === lineKey.location}
                      onClick={() => setActiveLineKey(lineKey)}
                      id={`lineKey${lineKey.id}`}
                    >
                      <ListItemIcon>
                        <LineKeyIcon lineKeyId={lineKey.type.lineKeyId} />
                      </ListItemIcon>
                      {lineKey.type.lineKeyId === LineKeyType.EMPTY ? (
                        <ListItemText primary="Empty" secondary="Click to set up" />
                      ) : (
                        <ListItemText
                          primary={lineKey.label || <i>Default label</i>}
                          secondary={lineKey.value ? lineKey.value.replace(`${pbx.context}-`, "") : undefined}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            </EditorList>
          </TabPanel>
        ))}
      </Box>
      <EditorSidebar showForm={!!activeLineKey}>
        <LineKeyForm
          lineKey={activeLineKey}
          lineKeyTemplate={lineKeyTemplate}
          lineKeyTemplateDispatch={lineKeyTemplateDispatch}
          deviceLineKeys={deviceLineKeys}
          specs={specs}
        />
      </EditorSidebar>
    </Editor>
  );
}

LineKeyEditor.propTypes = {
  lineKeyTemplate: PropTypes.object,
  specs: PropTypes.object,
  lineKeyTemplateDispatch: PropTypes.func,
  customHeader: PropTypes.node,
};

export default LineKeyEditor;
